import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  IconButton
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, SkillsGrid, SkillCard, SkillCardContent, SkillTitle, SkillDetailsGrid, ItalicText, ActionButtons } from './styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';
import { skillsApi } from '../../../services/api';
import { 
  SKILL_TYPES, 
  getStatusColor, 
  getStatusTextColor, 
  getStatusText 
} from '../../../constants/skillTypes';

const Skills = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, skill: null });
  const [isLoading, setIsLoading] = useState(true);

  const fetchSkills = async () => {
    try {
      setIsLoading(true);
      const data = await skillsApi.getAllSkills();
      setSkills(data);
    } catch (err) {
      showAlert('Failed to load skills.', 'error', err);
      console.error('Error fetching skills:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const handleAddClick = () => {
    navigate('/skills-matrix/skills/new');
  };

  const handleEditClick = (skill) => {
    navigate(`/skills-matrix/skills/${skill.id}`);
  };

  const handleDeleteSkill = (skill) => {
    setDeleteModal({ open: true, skill });
  };

  const confirmDelete = async () => {
    const skillToDelete = deleteModal.skill;
    setDeleteModal({ open: false, skill: null });
    
    if (!skillToDelete?.id) {
      showAlert('Invalid skill data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await skillsApi.deleteSkill(skillToDelete.id);
      await fetchSkills();
      showAlert('Skill deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting skill:', err);
      showAlert(err.message || 'Failed to delete skill. Please try again.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
                         skill.description?.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesTypes = selectedTypes.length === 0 || selectedTypes.includes(skill.type);
    return matchesSearch && matchesTypes;
  });

  const renderFilterSection = (title, items, selectedItems, onItemClick) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="subtitle2" sx={{ color: '#475569', fontWeight: 500, fontSize: '14px' }}>
          {title}
        </Typography>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {items.map(item => (
            <Chip
              key={item}
              label={getStatusText(item)}
              onClick={() => onItemClick(item)}
              sx={{
                backgroundColor: selectedItems.includes(item) ? getStatusColor(item) : 'white',
                color: selectedItems.includes(item) ? getStatusTextColor(item) : '#64748b',
                border: '1px solid',
                borderColor: selectedItems.includes(item) ? getStatusTextColor(item) : '#E2E8F0',
                '&:hover': {
                  backgroundColor: selectedItems.includes(item) ? getStatusColor(item) : '#F8FAFC',
                },
                height: '32px',
                '& .MuiChip-label': {
                  fontSize: '14px',
                  padding: '0 16px',
                  fontWeight: 500
                }
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix')}>
            <FaArrowLeft /> Back to Skills Matrix
          </BackButton>
          <Title>Skills Configuration</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Skills"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />

              {renderFilterSection(
                'Filter by Type',
                Object.values(SKILL_TYPES),
                selectedTypes,
                (type) => {
                  setSelectedTypes(prev => 
                    prev.includes(type)
                      ? prev.filter(t => t !== type)
                      : [...prev, type]
                  );
                }
              )}
            </Box>

            <HeaderButton 
              onClick={handleAddClick}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Skill
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div style={{ 
                color: '#64748B', 
                fontSize: '14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{ fontWeight: 600 }}>
                  {filteredSkills.length}
                </span>
                {filteredSkills.length === 1 ? 'skill' : 'skills'} found
              </div>
              {filteredSkills.length === 0 ? (
                <EmptyState
                  title="No skills found"
                  description={searchTerm || selectedTypes.length > 0 ? 
                    "No skills match your search criteria. Try adjusting your filters or search term." : 
                    "No skills have been added yet. Click the '+ Add Skill' button to add your first skill."}
                />
              ) : (
                <SkillsGrid>
                  {filteredSkills.map((skill) => (
                    <SkillCard key={skill.id}>
                      <SkillCardContent>
                        <SkillTitle>
                          {skill.name}
                          <div style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: getStatusColor(skill.type),
                            color: getStatusTextColor(skill.type),
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {getStatusText(skill.type)}
                          </div>
                        </SkillTitle>
                        <SkillDetailsGrid>
                          {skill.description ? skill.description : <ItalicText>No description provided</ItalicText>}
                        </SkillDetailsGrid>
                        
                        {/* Follow-up Questions Count */}
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '12px',
                          borderTop: '1px solid #E2E8F0',
                          fontSize: '13px',
                          color: '#475569'
                        }}>
                          {skill.meta?.followUpQuestions > 0 ? (
                            <span style={{ fontWeight: '500' }}>
                              <span style={{ fontWeight: '700', fontSize: '15px' }}>{skill.meta.followUpQuestions}</span> follow-up {skill.meta.followUpQuestions === 1 ? 'question' : 'questions'} defined
                            </span>
                          ) : (
                            <span style={{ fontStyle: 'italic', color: '#94A3B8' }}>
                              No follow-up questions defined
                            </span>
                          )}
                        </div>

                        <ActionButtons>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditClick(skill)}
                          >
                            <FaEdit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteSkill(skill)}
                            color="error"
                          >
                            <FaTrash />
                          </IconButton>
                        </ActionButtons>
                      </SkillCardContent>
                    </SkillCard>
                  ))}
                </SkillsGrid>
              )}
            </>
          )}
        </MainContainer>
      </Content>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, skill: null })}
        onConfirm={confirmDelete}
        title="Delete Skill"
        message={`Are you sure you want to delete the skill "${deleteModal.skill?.name}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default Skills; 